import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "../../Ui/index.jsx";
import { apiPost, returnToOwnCompany } from "../../Api/Client.js";
import { leaveSupport } from "../../Api/leaveSupport.js";

const RETURN_TO_KEY = "atg-impersonate-return-to";

// Shown while an admin is looking at the intranet as someone else.
function ViewingAsBar({ me }) {
  const [error, setError] = useState("");
  const exit = async () => {
    setError("");
    // Leave only if the session really switched back; a failed exit must not look like a successful one.
    try {
      await apiPost("/Users/admin-panel/stop-impersonate/", {});
    } catch (e) {
      setError(`Could not exit back to admin: ${e?.message || "request failed"}. You are still viewing as this user; sign out and back in if this persists.`);
      return;
    }
    // Back to where "Login as" was started (the People list), not the other person's home page.
    let returnTo = "/home/";
    try {
      returnTo = sessionStorage.getItem(RETURN_TO_KEY) || returnTo;
      sessionStorage.removeItem(RETURN_TO_KEY);
    } catch { /* private mode */ }
    window.location.href = returnTo;
  };
  return (
    <div className="Atg-Impersonate-Bar">
      <span>👁 Viewing as <b>{me.user?.fullName || me.user?.username}</b> — you're seeing exactly what they see (nav, access, masked contacts).</span>
      {error && <span role="alert" className="Atg-Impersonate-Bar__alert">{error}</span>}
      <Button className="Atg-Impersonate-Exit" onClick={exit}>Exit — back to admin</Button>
    </div>
  );
}

// Shown to platform staff working inside another company during a recorded support session.
function SupportBar({ me }) {
  const [error, setError] = useState("");
  const leave = async () => {
    setError("");
    const left = await leaveSupport({
      close: () => apiPost(`/EnterpriseCore/platform/support/${me.support.id}/close/`, {}),
      restoreCompany: () => returnToOwnCompany(me.user?.ownCompanyId),
      goTo: () => { window.location.href = "/platform/"; },
    });
    if (!left) setError("Could not leave this company. Please try again.");
  };
  return (
    <div className="Atg-Impersonate-Bar Atg-Impersonate-Bar--support">
      {/* Only changes (and a few declared-sensitive reads) reach the trail, so say that, not "everything". */}
      <span>Platform support inside <b>{me.activeTenant?.name}</b> until {new Date(me.support.endsAt).toLocaleTimeString()}. Changes you make are recorded in their trail.</span>
      {error && <span role="alert" className="Atg-Impersonate-Bar__alert">{error}</span>}
      <Button className="Atg-Impersonate-Exit" onClick={leave}>Leave this company</Button>
    </div>
  );
}

// The strip under the top bar for the two special sessions, and the one-line notice when a load partly failed.
export function SessionBars({ me, errors }) {
  // A refused request (403) means the page model did its job, and an auditor-only account is refused everywhere outside
  // /audit by design: neither is a fault to show.
  const fault = me?.auditorOnly ? null : errors.find((item) => item.status && item.status !== 401 && item.status !== 403);
  return (
    <>
      {me?.impersonating && <ViewingAsBar me={me} />}
      {me?.support && <SupportBar me={me} />}
      {fault && (
        <div className="Atg-Error-Bar">
          <AlertTriangle size={16} />
          <span>{fault.message}</span>
        </div>
      )}
    </>
  );
}
